import { Search } from "lucide-react"
import { useImperativeHandle, useRef } from "react"

import useKeyboardShortcut, { Key } from "../../../../hooks/useKeyboardShortcut"
import { classNames } from "../../../../lib/classNames"
import { KeyboardTile } from "../../informational/KeyboardTile"
import {
  TextInput,
  TextInputProps,
  TextInputVariant,
  TextInputVariants,
} from "../text/TextInput"

const MAX_SEARCH_INPUT_LENGTH = 100

interface SearchInputProps extends TextInputProps {
  containerClassName?: string
  keyboardShortcutKey?: Key
  hideKeyboardTile?: boolean
  variant?: TextInputVariant
}

const KEY_DISPLAY_SYMBOLS = {
  Slash: "/",
} as const

export const SearchInput = ({
  containerClassName,
  className,
  placeholder,
  keyboardShortcutKey = "/",
  hideKeyboardTile = false,
  variant = TextInputVariants.DEFAULT,
  ref,
  ...otherProps
}: SearchInputProps) => {
  const innerRef = useRef<HTMLInputElement>(null)
  useKeyboardShortcut(keyboardShortcutKey, (event) => {
    event?.preventDefault()
    innerRef.current?.focus()
  })

  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, [])

  const keyToDisplaySymbol = (key: string) =>
    KEY_DISPLAY_SYMBOLS[key as keyof typeof KEY_DISPLAY_SYMBOLS] ||
    key.toUpperCase()

  return (
    <div className={classNames("group relative", containerClassName)}>
      <TextInput
        leftIcon={variant === TextInputVariants.SUBTLE ? undefined : Search}
        variant={variant}
        className={classNames("flex-1", className)}
        placeholder={placeholder ?? "Search"}
        ref={innerRef}
        maxLength={MAX_SEARCH_INPUT_LENGTH}
        {...otherProps}
      />
      {!hideKeyboardTile && (
        <KeyboardTile
          color="gray"
          keyboardKey={keyToDisplaySymbol(keyboardShortcutKey)}
          className="absolute inset-y-2.5 right-5 hidden group-hover:flex"
        />
      )}
    </div>
  )
}
